import { useContext } from '@nuxtjs/composition-api'
import type VueI18n from 'vue-i18n'

export type ValidationFunc = (v: string) => string | true
type ValidationRules = Record<
  'idRules' | 'commonPasswordRules' | 'newPasswordRules' | 'emailRules' | 'requiredRules',
  readonly ValidationFunc[]
>

const rules = (i18n: VueI18n): ValidationRules => ({
  /** ログインフォームなどのIDの入力ルール */
  idRules: [
    (v: string) => !!v || i18n.tc('validateMessages.id.required', 1),
    (v: string) => !v || /^[a-zA-Z0-9]+$/.test(v) || i18n.tc('validateMessages.id.character', 1),
    (v: string) => !v || (v.length >= 4 && v.length <= 190) || i18n.tc('validateMessages.id.length', 1)
  ],
  commonPasswordRules: [
    (v: string) => !!v || i18n.tc('validateMessages.password.required', 1),
    (v: string) => !v || /^[a-zA-Z0-9]+$/.test(v) || i18n.tc('validateMessages.password.character', 1),
    (v: string) => !v || (v.length >= 4 && v.length <= 255) || i18n.tc('validateMessages.password.length', 1)
  ],
  newPasswordRules: [
    (v: string) => !!v || i18n.tc('validateMessages.password.required', 1),
    (v: string) => !v || /^[a-zA-Z0-9]+$/.test(v) || i18n.tc('validateMessages.password.character', 1),
    /*
     * NOTE:
     * 下記3つを含めたのが正しいパスワードのルールだが、
     * これを途中から適用すると既存パスワードでログイン・変更できなくなる為、新しいパスワード設定時のみに適用するようにしている
     * 対応issues: https://gitlab.com/mates-pay/app/new_reco/reco_for_parent/-/issues/497, https://gitlab.com/mates-pay/app/new_reco/reco_for_parent/-/issues/505
     */
    (v: string) => !v || (v.length >= 8 && v.length <= 16) || i18n.tc('validateMessages.newPassword.length', 1),
    (v: string) => !v || /[0-9]/.test(v) || i18n.tc('validateMessages.newPassword.atLeastOne.number', 1),
    (v: string) => !v || /[A-Za-z]/.test(v) || i18n.tc('validateMessages.newPassword.atLeastOne.alphabet', 1)
  ],
  emailRules: [
    (v: string) => !!v || i18n.tc('validateMessages.email.required', 1),
    (v: string) =>
      !v ||
      /^\w+[a-zA-Z0-9_.+-]*@[a-zA-Z0-9_.+-]+\.([a-zA-Z]*\.)??[a-zA-Z]+$/.test(v) ||
      i18n.tc('validateMessages.email.syntax', 1)
  ],
  requiredRules: [(v: string) => !!v || i18n.tc('validateMessages.required', 1)]
})

export const useValidation = () => {
  const { i18n } = useContext()

  return rules(i18n)
}
